import { IconButton, Stack, Typography } from '@mui/material';
import { GetContractPageDataResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { buildCurrencyLevel, convertCurrencyToUsd } from '@vy/services';
import { CURRENCIES } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined, vyDate } from '@vy/utils';
import { useExchangeRates } from '../../../../../hooks/requests/exchange-rates/use-exchange-rates';
import { FC, useMemo } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyLinearProgress } from '../../../../../components/new-design/data-display/VyLinearProgress/VyLinearProgress';
import { getCrmProviderFromIntegrationEntities } from '../../../../../hooks/requests/use-get-external-link';
import { formatContractOrigin } from './format-contract-origin';
import { useMenu } from '../../../../../hooks/use-menu';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate/use-vy-navigate';
import { useContractEstimator } from '../../../../customers/OldCustomerOverviewPage/CustomerOverviewPage';
import { formatRefNumber } from '../../../utils/formatRefNumber';
import { ContractDotMenu } from './ContractDotMenu';
import { ContractPdfDrawer } from './ContractPdfDrawer';
import { ContractStatCard } from './ContractStatCard/ContractStatCard';
import { contractPageSelectors, getSelectorAttribute } from '@vy/app-selectors';

type ContractEstimationsProps = {
  contract: GetContractPageDataResponse['contractPageData']['contract'];
  isContractEditable: boolean;
  isContractHasPhases?: boolean;
};

export const ContractEstimations: FC<ContractEstimationsProps> = ({
  contract,
  isContractEditable,
  isContractHasPhases,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const {
    isDialogOpen: isPdfDrawerOpen,
    handleOpen: handlePdfDrawerOpen,
    handleClose: handlePdfDrawerClose,
  } = useDialog();
  const navigate = useVyNavigate();

  const estimatedContractValue = useContractEstimator(contract, contract.plan);

  const customerCurrency = contract.customer.settings?.currency ?? CURRENCIES.Usd;
  const invoicesCurrency = buildCurrencyLevel({
    customerCurrency,
    contractCurrency: contract.currencyOverride,
  });

  const { data: exchangeRates } = useExchangeRates();

  const estimatedValueUsd = useMemo(() => {
    if (isNullOrUndefined(estimatedContractValue) || isNullOrUndefined(exchangeRates)) {
      return undefined;
    }

    return convertCurrencyToUsd(estimatedContractValue, customerCurrency, exchangeRates);
  }, [estimatedContractValue, customerCurrency, exchangeRates]);

  const totalAmountToPayUsd = useMemo(() => {
    if (isNullOrUndefined(contract.totalAmountToPay) || isNullOrUndefined(exchangeRates)) {
      return undefined;
    }

    return convertCurrencyToUsd(contract.totalAmountToPay, invoicesCurrency, exchangeRates);
  }, [contract.totalAmountToPay, invoicesCurrency, exchangeRates]);

  const refNumber = formatRefNumber(contract.id);
  const { passedDays, totalDays } = getTermProgress(contract.startDate, contract.endDate);

  const contractOriginProvider = getCrmProviderFromIntegrationEntities(contract.integrationEntities);

  return (
    <>
    <ContractStatCard
      cardLabel="Contract estimated value"
      cardHeaderAdditionalContent={
        <>
          <IconButton
            onClick={handleMenuOpen}
            sx={{ paddingRight: 0 }}
            disableRipple
            {...getSelectorAttribute(contractPageSelectors.header.contractMenuButton)}
          >
            <VyIcon icon="DotsMenuNew" />
          </IconButton>
          <ContractDotMenu
            contract={contract}
            isOpen={isOpen}
            anchorElement={anchorElement ?? null}
            handleMenuClose={handleMenuClose}
            billingData={contract.plan.billingData}
            isContractEditable={isContractEditable}
            isContractHasPhases={isContractHasPhases}
            isContractWasRenewed={contract.wasRenewed}
          />
        </>
      }
      mainValue={
        <Price className="text-[28px]" currency={CURRENCIES.Usd}>
          {estimatedValueUsd}
        </Price>
      }
      subItems={[`Ref no. ${refNumber}`, `Origin ${formatContractOrigin(contractOriginProvider)}`]}
      navLinks={[
        {
          label: 'Customer',
          value: contract.customer.name,
          onClick: () => navigate(`/customers/${contract.customer.id}/invoices`),
          selector: 'contract-estimations-customer-link',
        },
        {
          label: 'View Docs.',
          onClick: handlePdfDrawerOpen,
          selector: 'contract-estimations-view-docs',
        },
      ]}
    >
      <Stack spacing={3}>
        <VyLinearProgress
          currentValue={contract.revenue}
          totalValue={contract.totalAmountToPay}
          progressColor="#81B3FF"
          labelPlacement="bottom"
          renderLabel={() => (
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: '16px', color: '#7E8796' }}>
                Billed amount
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: '16px', color: '#7E8796' }}>
                <Price className="text-[12px]" currency={customerCurrency}>
                  {totalAmountToPayUsd}
                </Price>
              </Typography>
            </Stack>
          )}
        />
        {isNotNullOrUndefined(contract.endDate) && (
          <VyLinearProgress
            currentValue={passedDays}
            totalValue={totalDays}
            progressColor="#81B3FF"
            labelPlacement="bottom"
            renderLabel={() => (
              <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: '16px', color: '#7E8796' }}>
                Term progress
              </Typography>
            )}
          />
        )}
      </Stack>
    </ContractStatCard>
    <ContractPdfDrawer open={isPdfDrawerOpen} onClose={handlePdfDrawerClose} contractId={contract.id} />
    </>
  );
};

function getTermProgress(contractStartDate: Date, contractEndDate?: Date) {
  const startDate = vyDate(contractStartDate);
  const endDate = vyDate(contractEndDate);

  const totalDays = endDate.diff(startDate, 'days');
  const passedDays = vyDate().diff(startDate, 'days');

  return {
    totalDays,
    passedDays,
  };
}
